import { Component, inject, input, model, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';

import { MemberService } from '../../services/member';
import { ToastService } from '../../services/toast';

/**
 * "Add the member's e-mail" — offered at the moment a payment is refused for the want of one.
 *
 * Generali takes the payer's e-mail as a required form field and rejects the request when it is
 * blank, so a member onboarded without one cannot be charged at all. Re-onboarding somebody to
 * add an address is absurd; this captures it in place and the caller retries the payment.
 *
 * Saved through the member API (partner-scoped server-side), so the address sticks to the member
 * and every later receipt and certificate reaches them too — not just this one payment.
 */
@Component({
  selector: 'app-member-email-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, ButtonModule, InputTextModule],
  template: `
    <p-dialog [visible]="visible()" (visibleChange)="visible.set($event)" [modal]="true"
      [draggable]="false" [resizable]="false" [dismissableMask]="!saving()"
      header="Add e-mail address" [style]="{ width: '420px' }" [breakpoints]="{ '420px': '94vw' }">
      <div class="med">
        <p class="med-note">
          <i class="pi pi-info-circle"></i>
          {{ gatewayName() || 'This payment gateway' }} needs an e-mail address for
          <strong>{{ memberName() || 'this member' }}</strong> before the premium can be collected.
        </p>

        <label for="med-email">E-mail address</label>
        <input id="med-email" pInputText type="email" placeholder="name@example.com"
          [(ngModel)]="email" (keyup.enter)="save()" [disabled]="saving()" autocomplete="email" />

        @if (error()) { <small class="med-err">{{ error() }}</small> }
        <small class="med-hint">Saved to the member, so their receipt and certificate reach them too.</small>

        <div class="med-actions">
          <p-button label="Cancel" [text]="true" severity="secondary"
            [disabled]="saving()" (onClick)="visible.set(false)" />
          <p-button [label]="saving() ? 'Saving…' : 'Save & continue'"
            [icon]="saving() ? 'pi pi-spin pi-spinner' : 'pi pi-check'"
            [disabled]="saving()" (onClick)="save()" />
        </div>
      </div>
    </p-dialog>
  `,
  styles: [`
    .med { display: flex; flex-direction: column; gap: 8px; }
    .med-note {
      display: flex; align-items: flex-start; gap: 8px; margin: 0 0 6px;
      padding: 10px 12px; border: 1px solid #bfdbfe; background: #eff6ff;
      border-radius: 10px; font-size: 12.5px; color: #1e40af; line-height: 1.5;
    }
    .med-note i { font-size: 13px; margin-top: 1px; flex: none; }
    label { font-size: 12.5px; font-weight: 600; color: var(--text, #1f2937); }
    input { width: 100%; }
    .med-err { color: #dc2626; font-size: 12.5px; }
    .med-hint { color: var(--muted, #6b7280); font-size: 12px; }
    .med-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
  `],
})
export class MemberEmailDialog {
  private members = inject(MemberService);
  private toast = inject(ToastService);

  readonly visible = model<boolean>(false);
  readonly memberId = input<number>(0);
  readonly memberName = input<string | null>(null);
  /** Named in the prompt so the reason is concrete rather than "a gateway". */
  readonly gatewayName = input<string | null>(null);

  /** The saved address — the host retries the payment on this. */
  readonly saved = output<string>();

  email = '';
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  /** Reset and open. Called by the host when a payment comes back needing an e-mail. */
  open(): void {
    this.email = '';
    this.error.set(null);
    this.saving.set(false);
    this.visible.set(true);
  }

  save(): void {
    const value = (this.email || '').trim();

    // Checked here only to save a round trip; the server validates it again, and the server's
    // answer is the one that decides.
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      this.error.set('Enter a valid e-mail address.');
      return;
    }
    if (this.memberId() <= 0 || this.saving()) return;

    this.saving.set(true);
    this.error.set(null);

    this.members.updateEmail(this.memberId(), value).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.error.set(res?.message || 'Could not save the e-mail address.');
          return;
        }
        this.toast.success('E-mail address saved.');
        this.visible.set(false);
        this.saved.emit(value);
      },
      error: () => {
        this.saving.set(false);
        this.error.set('Could not save the e-mail address. Please try again.');
      },
    });
  }
}
